import { create } from "zustand";

/**
 * EU Evidence Chain Store (v1)
 * 
 * Manages evidence chain items, integrity scoring, admissibility levels,
 * and chain volatility calculations for cross-border judicial compatibility.
 */
export interface EvidenceItem {
  id: string;
  hash: string;
  timestampISO: string;
  producer: string;
  region: string;
  severity: number;
}

export interface EvidenceChainStoreState {
  evidenceItems: EvidenceItem[];
  chainLength: number;
  chainIntegrityScore: number; // 0-100
  admissibilityLevel: "low" | "medium" | "high";
  chainJitterMS: number;
  chainDriftMinutes: number;

  // Actions
  addEvidence: (hash: string, producer: string, region: string, severity: number) => void;
  removeEvidence: (id: string) => void;
  purgeLowSeverity: () => void;

  // Derived outputs
  getHashEntropy: () => number;
  getChainVolatility: () => number;
  getAdmissibilityLevel: () => "low" | "medium" | "high";

  // Export
  exportEvidenceChain: () => void;
}

export const useEvidenceChainStore = create<EvidenceChainStoreState>((set, get) => {
  const recomputeChain = () => {
    const state = get();
    const chainLength = state.evidenceItems.length;
    const chainVolatility = state.getChainVolatility();
    const avgSeverity = state.evidenceItems.length > 0
      ? state.evidenceItems.reduce((sum, item) => sum + item.severity, 0) / state.evidenceItems.length
      : 0;
    const chainIntegrityScore = Math.max(0, Math.min(100, 100 - chainVolatility - avgSeverity * 2));
    const admissibilityLevel = state.getAdmissibilityLevel();

    set({
      chainLength,
      chainIntegrityScore,
      admissibilityLevel,
    });
  };

  return {
    evidenceItems: [],
    chainLength: 0,
    chainIntegrityScore: 100,
    admissibilityLevel: "high",
    chainJitterMS: 0,
    chainDriftMinutes: 0,

    addEvidence: (hash, producer, region, severity) => {
      const state = get();
      const newItem: EvidenceItem = {
        id: `evidence_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        hash,
        timestampISO: new Date().toISOString(),
        producer,
        region,
        severity,
      };

      set({
        evidenceItems: [...state.evidenceItems, newItem],
      });

      recomputeChain();
    },

    removeEvidence: (id) => {
      const state = get();
      set({
        evidenceItems: state.evidenceItems.filter((item) => item.id !== id),
      });

      recomputeChain();
    },

    purgeLowSeverity: () => {
      const state = get();
      set({
        evidenceItems: state.evidenceItems.filter((item) => item.severity >= 2),
      });

      recomputeChain();
    },

    getHashEntropy: () => {
      const state = get();
      if (state.evidenceItems.length === 0) return 0;

      const hashLengths = state.evidenceItems.map((item) => item.hash.length);
      const avgLength = hashLengths.reduce((sum, len) => sum + len, 0) / hashLengths.length;
      const variance = hashLengths.reduce((sum, len) => sum + Math.pow(len - avgLength, 2), 0) / hashLengths.length;
      return Math.sqrt(variance);
    },

    getChainVolatility: () => {
      const state = get();
      return (state.chainJitterMS / 10) + (state.chainDriftMinutes * 0.5);
    },

    getAdmissibilityLevel: () => {
      const state = get();
      if (state.chainIntegrityScore >= 80) return "high";
      if (state.chainIntegrityScore >= 50) return "medium";
      return "low";
    },

    exportEvidenceChain: () => {
      const state = get();
      const report = {
        evidenceItems: state.evidenceItems,
        chainLength: state.chainLength,
        chainIntegrityScore: state.chainIntegrityScore,
        admissibilityLevel: state.admissibilityLevel,
        chainVolatility: state.getChainVolatility(),
        chainJitterMS: state.chainJitterMS,
        chainDriftMinutes: state.chainDriftMinutes,
        hashEntropy: state.getHashEntropy(),
      };

      const dateStr = new Date().toISOString().split("T")[0].replace(/-/g, "");
      const blob = new Blob([JSON.stringify(report, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `evidenceChain_${dateStr}.json`;
      a.click();
      URL.revokeObjectURL(url);
    },
  };
});











